import { useMemo, useRef, useState } from "react";
import { Plus } from "lucide-react";
import api from "../lib/api";
import { useCreateDialog } from "../hooks/useCreateDialog";
import { GOAL_STATUS_OPTIONS_DRAWN } from "../lib/goalStatus";
import { FailureCard, SavedCard } from "./GoalNotice";
import { ActionButton, SecondaryAction } from "./kit";
import { SelectField, TEXTAREA_STYLE, GoalTitleField, GoalCategoryField, GoalHorizonField, GoalDueDateField, GoalNotesField } from "./GoalSheetFields";
import SettingsSheet from "./SettingsSheet";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";

/**
 * "New goal" on the new look (#1219, D283, D292 for the player words): a kit sheet with the anatomy of
 * `CoachPlayerGoals.dc.html`'s "Assign goal" (Cancel, then a gold primary),
 * holding the player's own fields and posting the same `POST /goals` body the
 * old `NewGoalDialog` posts. Failures show in the sheet, never as a toast
 * (D57): a missing title says "Goal title required" under the field, and a
 * failed save says "Everything you typed is still here. Try again." with every
 * value kept.
 *
 * The goal title is the one required field; it posts as both `goal_title` and
 * `target`, as before. Its own `useCreateDialog()` instance (see
 * `DevelopmentPlan.jsx`'s note on why it is never shared with the page's).
 */

const EMPTY = {
  target: "",
  target_date: "",
  status: "not_started",
  horizon: "short",
  category: "general",
  milestones_text: "",
  notes: "",
};

export default function NewGoalSheet({ onCreated }) {
  const [open, setOpen] = useCreateDialog();
  const [f, setF] = useState(EMPTY);
  const [titleError, setTitleError] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [added, setAdded] = useState(false);
  // #1299 (D303): Save scrolls to the first missing field and focuses it.
  const titleRef = useRef(null);
  // `attempt` counts failed Saves, so pressing Save again with the title still
  // empty is a new error set and takes the player back to the field.
  const [attempt, setAttempt] = useState(0);
  const focusErrors = useMemo(() => ({ title: titleError ? attempt : 0 }), [titleError, attempt]);
  const armFocus = useFocusFirstInvalid(titleRef, focusErrors);

  const set = (patch) => setF((cur) => ({ ...cur, ...patch }));

  const onOpenChange = (next) => {
    setOpen(next);
    if (next) setAdded(false);
    if (!next) {
      setTitleError(false);
      setSaveError(false);
    }
  };

  const create = async () => {
    if (saving) return;
    armFocus();
    if (!f.target.trim()) {
      setTitleError(true);
      setAttempt((n) => n + 1);
      return;
    }
    const payload = {
      goal_title: f.target,
      target: f.target,
      description: "",
      target_date: f.target_date || null,
      progress_percentage: 0,
      status: f.status,
      horizon: f.horizon,
      category: f.category,
      notes: f.notes,
      milestones: f.milestones_text.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    setSaving(true);
    setSaveError(false);
    try {
      await api.post("/goals", payload);
      setOpen(false);
      setF(EMPTY);
      setTitleError(false);
      setAdded(true);
      await onCreated?.();
    } catch {
      setSaveError(true);
    }
    setSaving(false);
  };

  return (
    <>
      <SecondaryAction variant="navy-outline" data-testid="dev-new-goal" onClick={() => onOpenChange(true)}>
        <Plus size={18} strokeWidth={2} aria-hidden="true" style={{ marginRight: 8 }} />
        New goal
      </SecondaryAction>
      {added && <SavedCard data-testid="goal-added">Goal added</SavedCard>}
      <SettingsSheet open={open} onOpenChange={onOpenChange} title="New development goal" data-testid="new-goal-sheet">
        <div ref={titleRef}>
          <GoalTitleField
            value={f.target}
            onChange={(v) => {
              set({ target: v });
              if (titleError && v.trim()) setTitleError(false);
            }}
            error={titleError ? "Goal title required" : null}
          />
          {titleError && (
            <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.5, color: "var(--nw-ink-muted)" }}>Everything else typed is still in the sheet.</div>
          )}
        </div>
        <GoalCategoryField value={f.category} onChange={(v) => set({ category: v })} />
        <GoalHorizonField value={f.horizon} onChange={(v) => set({ horizon: v })} />
        <GoalDueDateField value={f.target_date} onChange={(v) => set({ target_date: v })} />
        <SelectField id="goal-status" label="Status" value={f.status} onChange={(v) => set({ status: v })} testId="goal-status">
          {GOAL_STATUS_OPTIONS_DRAWN.map((s) => (
            <option key={s.value} value={s.value}>
              {s.label}
            </option>
          ))}
        </SelectField>
        <div>
          <label htmlFor="goal-actions" className="nw-label" style={{ marginBottom: 4, display: "block" }}>
            Actions (one per line)
          </label>
          <textarea
            id="goal-actions"
            data-testid="goal-actions"
            rows={4}
            value={f.milestones_text}
            onChange={(e) => set({ milestones_text: e.target.value })}
            placeholder={"4x weekly net sessions\nVideo review 10 innings\nCoach 1:1 fortnightly"}
            className="nw-kit-input"
            style={TEXTAREA_STYLE}
          />
        </div>
        <GoalNotesField value={f.notes} onChange={(v) => set({ notes: v })} />
        {saveError && <FailureCard data-testid="goal-save-error" title="Failed to create goal" body="Everything you typed is still here. Try again." />}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SecondaryAction variant="boxed" onClick={() => onOpenChange(false)} disabled={saving} data-testid="goal-cancel-btn" style={{ width: "auto", flexShrink: 0 }}>
            Cancel
          </SecondaryAction>
          <ActionButton
            onClick={create}
            disabled={saving}
            data-testid="goal-create-btn"
            // The board's greyed primary (#E2E8F0 on #64748B), literal so a unit test can read it.
            style={saving ? { background: "#E2E8F0", color: "#64748B", opacity: 1 } : undefined}
          >
            {saving ? "Adding…" : "Add goal"}
          </ActionButton>
        </div>
      </SettingsSheet>
    </>
  );
}
